import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Eye, EyeSlash, ArrowRight, ArrowLeft, Storefront, UsersThree, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { GLIMState, MartianWordmark, BRAND_COPY } from '../brand';

// Persona onboarding simplification -- Creator is a Phase 2 product and
// is deliberately not offered to new users at signup. Creator
// functionality itself (CreatorWorkspacePage, its nav, its routes) is
// untouched; this list only controls what a NEW user can select here.
//
// Visual-consistency pass: dropped the per-persona `color` field. It
// drove the old dark screen's hardcoded blue/orange (#1A56DB/#F59E0B)
// icon + selected-state styling -- exactly the "legacy visual
// treatment as primary brand colour" this stage removes. Selection now
// uses one shared accent-on-select state (see the persona screen JSX
// below), matching how the rest of the product marks an active state.
const PERSONAS = [
  { id: 'brand', title: 'Brand or D2C', icon: Storefront, desc: 'I manage a brand and want to track and improve how AI recommends us across search, voice, and social.', examples: 'Skincare  Fashion  F&B  Health  Electronics  EdTech  FinTech  D2C' },
  { id: 'agency', title: 'Digital Agency', icon: UsersThree, desc: 'I manage multiple brand clients and need a portfolio view with white-label reporting.', examples: 'SEO firms  Performance agencies  Social agencies  Growth consultancies' },
];

export default function LoginPage() {
  const [step, setStep] = useState('auth'); // auth, persona
  const [isRegister, setIsRegister] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState('');
  // Final landing-page reset -- a domain submitted on the homepage
  // hero and confirmed on the Snapshot page arrives here via ?url=,
  // so the user never has to re-type it during onboarding (handed off
  // to BrandOnboardingPage via sessionStorage -- see
  // handleRegisterWithPersona below).
  const prefillDomain = new URLSearchParams(window.location.search).get('url') || '';

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        setStep('persona');
      } else {
        await login(email, password);
        toast.success('Welcome back');
        navigate('/');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Authentication failed'); }
    finally { setLoading(false); }
  };

  // Final landing-page reset -- removed the dead onboard1/2/3 wizard
  // this stage's own testing found unreachable (confirmed by real
  // click-through testing, not assumed): LoginPage is wrapped in
  // <PublicRoute>, which always redirects to "/" the instant `user`
  // becomes truthy, before LoginPage's own `step` state could ever
  // render those screens -- handlePersonaSelect (the only OTHER path
  // that could reach step:'onboard1') was itself never called from
  // anywhere. handleFinishOnboarding's POST /auth/onboarding call is
  // removed with it -- confirmed via a repo-wide search that no other
  // frontend code calls that endpoint.
  //
  // Only the 'brand' persona has a real post-signup destination that
  // needs the captured domain (BrandOnboardingPage, via /onboarding).
  // Agency and creator personas land correctly on their own real
  // dashboards (ClientsPage / CreatorWorkspacePage) through the
  // existing, untouched default redirect -- confirmed by testing the
  // agency signup path directly, not assumed.
  const handleRegisterWithPersona = async () => {
    setLoading(true);
    try {
      // Stashed BEFORE register() rather than navigated after it: a
      // direct navigate() (or an effect keyed on `user`) called after
      // register() loses a real render-order race against PublicRoute's
      // own redirect (PublicRoute is LoginPage's parent, so it always
      // re-renders and swaps LoginPage out in the same commit where
      // `user` becomes truthy, before LoginPage's own code can act on
      // that same change). sessionStorage sidesteps this entirely --
      // it's readable synchronously by PublicRoute the moment it
      // re-renders, regardless of LoginPage's mount state.
      // Website-discovery onboarding (Step 3) -- a captured domain now
      // means the visitor came here specifically to have Martian research
      // it, so the post-auth destination is /discovery (BrandDiscoveryPage,
      // which auto-starts research the moment `user` is truthy) rather
      // than the older manual /onboarding form. /onboarding itself is
      // untouched and still fully reachable directly -- only the
      // domain-carrying redirect target changed.
      if (selectedPersona === 'brand' && prefillDomain) {
        sessionStorage.setItem('martian_post_auth_redirect', `/discovery?domain=${encodeURIComponent(prefillDomain)}`);
      }
      await register(name, email, password, selectedPersona);
    } catch (err) { toast.error(err.response?.data?.detail || 'Registration failed'); }
    finally { setLoading(false); }
  };

  // ═══ PERSONA SELECTION ═══
  // Visual-consistency pass -- reuses the same warm-neutral funnel
  // tokens as LandingPage.js/SnapshotPage.js (--warm-canvas, --border,
  // --surface, --text-primary/secondary/tertiary, --accent,
  // --radius-card/button, --shadow-card) rather than the legacy dark
  // navy (#0A1628) + serif "Chivo" + hardcoded blue/orange screen this
  // replaces. LoginPage sits in the same pre-auth funnel as those pages
  // (Landing -> Snapshot -> Signup), so --warm-canvas -- explicitly
  // scoped in index.css to "the hero/snapshot funnel," never the
  // authenticated app -- is the correct token family here, not
  // --canvas-secondary (Briefing's own background once inside
  // DashboardLayout, a step further down the funnel). No logic, copy,
  // or persona behavior changed -- selectedPersona/handleRegisterWithPersona
  // are untouched.
  if (step === 'persona') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--warm-canvas)]">
        <div className="w-full max-w-2xl mx-4 animate-fade-in-up">
          <div className="flex items-center justify-center gap-2 mb-6">
            <GLIMState state="neutral" size={40} showLabel={false} label="Martian" motion={false} />
            <MartianWordmark size="sm" />
          </div>
          <h1 className="font-display text-2xl font-bold text-[var(--text-primary)] text-center mb-2 text-balance">Who are you building for?</h1>
          <p className="text-[13px] text-[var(--text-secondary)] text-center mb-8">Your answer shapes everything you see in Martian.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PERSONAS.map(p => {
              const Icon = p.icon;
              const selected = selectedPersona === p.id;
              return (
                <button key={p.id} onClick={() => setSelectedPersona(p.id)} data-testid={`persona-${p.id}`}
                  className={`p-5 rounded-[var(--radius-card)] border text-left transition-all duration-200 bg-[var(--surface)] ${
                    selected ? 'border-[var(--accent)] shadow-[var(--shadow-card)]' : 'border-[var(--border)] hover:border-[var(--warm-border-strong)]'
                  }`}
                  style={selected ? { backgroundColor: 'var(--accent-light)' } : undefined}>
                  <Icon size={26} weight={selected ? 'fill' : 'regular'} className={selected ? 'text-[var(--accent)] mb-3' : 'text-[var(--text-tertiary)] mb-3'} />
                  <h3 className="text-[15px] font-display font-bold text-[var(--text-primary)] mb-1">{p.title}</h3>
                  <p className="text-[12px] text-[var(--text-secondary)] leading-relaxed mb-3">{p.desc}</p>
                  <p className="text-[10px] text-[var(--text-tertiary)] font-mono">{p.examples}</p>
                  {selected && <div className="flex items-center gap-1 mt-3 text-[10px] font-mono text-[var(--accent)]"><Check size={12} weight="bold" /> Selected</div>}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-8">
            <button onClick={() => setStep('auth')} className="text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] flex items-center gap-1"><ArrowLeft size={14} /> Back</button>
            <Button onClick={handleRegisterWithPersona} disabled={!selectedPersona || loading} data-testid="continue-persona-btn"
              className="bg-[var(--text-primary)] hover:bg-[var(--text-secondary)] text-white font-semibold rounded-[var(--radius-button)] px-8">
              {loading ? 'Creating...' : 'Continue'} <ArrowRight size={14} className="ml-2" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ═══ AUTH FORM ═══
  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[var(--warm-canvas)]">
      {/* Entry is GLIM-led and minimal: the face + approved lockup, no generic AI imagery. */}
      <div className="relative z-10 w-full max-w-md mx-4 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow-card)] p-8 animate-fade-in-up" data-testid="auth-form-container">
        <div className="flex flex-col items-center gap-3 mb-8 text-center">
          <GLIMState state={loading ? 'thinking' : 'neutral'} size={88} showLabel={false} label={loading ? 'Signing in' : 'Ready'} />
          <h1 className="m-0 flex flex-col items-center gap-1">
            <MartianWordmark size="lg" />
            <span className="text-[12px] font-medium text-[var(--text-tertiary)]">{BRAND_COPY.descriptor}</span>
          </h1>
        </div>
        <h2 className="text-lg font-bold text-[var(--text-primary)] mb-1">{isRegister ? 'Create Account' : 'Welcome Back'}</h2>
        <p className="text-sm text-[var(--text-tertiary)] mb-6">{isRegister ? 'Start managing your AI discoverability' : 'Sign in to your dashboard'}</p>
        <form onSubmit={handleAuth} className="space-y-4">
          {isRegister && (
            <div><label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Full Name</label>
              <Input data-testid="register-name-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" required={isRegister} className="bg-white border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)]" />
            </div>
          )}
          <div><label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Email</label>
            <Input data-testid="login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@agency.com" required className="bg-white border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)]" />
          </div>
          <div><label className="block text-xs text-[var(--text-tertiary)] mb-1.5 uppercase tracking-wider font-mono">Password</label>
            <div className="relative">
              <Input data-testid="login-password-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" required className="bg-white border-[var(--border)] text-[var(--text-primary)] placeholder:text-[var(--text-placeholder)] pr-10" />
              <button type="button" data-testid="toggle-password-visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Button data-testid="auth-submit-button" type="submit" disabled={loading} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white font-medium h-11">
            {loading ? 'Processing...' : isRegister ? 'Continue' : 'Sign In'} {!loading && <ArrowRight size={16} className="ml-2" />}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <button data-testid="toggle-auth-mode" className="text-sm text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" onClick={() => setIsRegister(!isRegister)}>
            {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
          </button>
        </div>
      </div>
    </div>
  );
}
